//
/* GS://PROTOCOLO
 * Arquivo: src/components/forms/AddAccountPayableForm.jsx
 * Versão: 2.0 – Inclusão de Categorização Financeira
 * Objetivo: Garantir que toda conta tenha uma categoria para o DRE.
 */

import React, { useState, useEffect } from 'react';
import { addAccountPayable, getSuppliers, getFinancialCategories } from '../../services/firestoreService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Timestamp, doc } from 'firebase/firestore'; 
import { db } from '../../firebase/config';

const AddAccountPayableForm = ({ onClose, onAccountAdded }) => {
    const [description, setDescription] = useState('');
    const [amountInput, setAmountInput] = useState(''); 
    const [amountRaw, setAmountRaw] = useState(0);
    const [dueDate, setDueDate] = useState('');
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]); // Padrão: Hoje
    const [supplierId, setSupplierId] = useState('');
    const [categoryId, setCategoryId] = useState(''); // NOVO
    const [status, setStatus] = useState('pending');
    const [notes, setNotes] = useState('');
    
    const [suppliers, setSuppliers] = useState([]);
    const [categories, setCategories] = useState([]); // NOVO
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [suppData, catData] = await Promise.all([
                    getSuppliers(),
                    getFinancialCategories()
                ]);
                setSuppliers(suppData);
                // Filtra apenas categorias de DESPESA (expense)
                setCategories(catData.filter(c => c.type === 'expense'));
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
                toast.error("Erro ao carregar fornecedores ou categorias.");
            } finally {
                setLoadingData(false);
            }
        };
        loadData();
    }, []);

    const handleAmountChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); 
        let cents = parseInt(value, 10);
        if (isNaN(cents)) cents = 0;
        const paddedCents = String(cents).padStart(3, '0'); 
        const integerPart = paddedCents.slice(0, -2);
        const decimalPart = paddedCents.slice(-2);
        setAmountInput(`${parseInt(integerPart, 10).toLocaleString('pt-BR')},${decimalPart}`);
        setAmountRaw(cents);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalAmount = amountRaw / 100;

        // Validação rigorosa
        if (!description || !finalAmount || !dueDate || !issueDate || !supplierId || !categoryId) {
            toast.warning("Preencha todos os campos obrigatórios, incluindo Categoria.");
            return;
        }

        const newAccount = {
            description,
            amount: finalAmount,
            dueDate: Timestamp.fromDate(new Date(dueDate)),
            issueDate: Timestamp.fromDate(new Date(issueDate)),
            supplierRef: doc(db, 'suppliers', supplierId),
            categoryRef: doc(db, 'financial_categories', categoryId), // Vínculo Crítico
            status,
            notes,
            // Campos de controle de baixa (iniciam vazios)
            paymentDate: null,
            paidAmount: 0,
        };

        try {
            await addAccountPayable(newAccount);
            toast.success("Conta a pagar registrada com sucesso!");
            onAccountAdded();
            onClose();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error(`Erro: ${error.message}`);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6 border-b pb-2">
                <h2 className="text-xl font-bold text-gray-800">Nova Conta a Pagar</h2>
                <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 font-bold text-xl">&times;</button>
            </div>

            {loadingData ? (
                <p className="text-center py-4">Carregando dados...</p>
            ) : (
                <div className="space-y-4">
                    {/* Descrição */}
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Descrição *</label>
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2 text-gray-700 focus:outline-none focus:border-brand-gold"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ex: Aluguel da Loja"
                            required
                        />
                    </div>

                    {/* Valor e Data Vencimento */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Valor (R$) *</label>
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2 text-gray-700 font-mono"
                                value={amountInput}
                                onChange={handleAmountChange}
                                placeholder="0,00"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Vencimento *</label>
                            <input
                                type="date"
                                className="w-full border rounded px-3 py-2 text-gray-700"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* Fornecedor e Categoria */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Fornecedor *</label>
                            <select
                                className="w-full border rounded px-3 py-2 text-gray-700 bg-white"
                                value={supplierId}
                                onChange={(e) => setSupplierId(e.target.value)}
                                required
                            >
                                <option value="">Selecione...</option>
                                {suppliers.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Categoria *</label>
                            <select
                                className="w-full border rounded px-3 py-2 text-gray-700 bg-white"
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value)}
                                required
                            >
                                <option value="">Selecione...</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Data Emissão e Status */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Data Emissão</label>
                            <input
                                type="date"
                                className="w-full border rounded px-3 py-2 text-gray-700"
                                value={issueDate}
                                onChange={(e) => setIssueDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">Status Inicial</label>
                            <select
                                className="w-full border rounded px-3 py-2 text-gray-700 bg-white"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="pending">Pendente</option>
                                <option value="paid">Já Pago (Histórico)</option>
                            </select>
                        </div>
                    </div>

                    {/* Observações */}
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Observações</label>
                        <textarea
                            className="w-full border rounded px-3 py-2 text-gray-700"
                            rows="2"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        ></textarea>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-brand-gold text-brand-black rounded hover:bg-yellow-500 font-bold"
                        >
                            Salvar Conta
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};

export default AddAccountPayableForm;